import React, { useState, useRef, useEffect } from "react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from "@/modern/components/ui/dropdown-menu";
import { cn } from "@/modern/utils";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { ENTITY_CONFIGS, EntityTypeKey } from "@/other/constants/EntityManagerConfig";
import { EntityRecord } from "@/types/entity";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys";
import { CopyToBoardDialog } from "../../CopyToBoardDialog";

export function EntityNameButton() {
  const { entityManager, project } = useSwivuBryntumContext();
  const [open, setOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [showCopyDialog, setShowCopyDialog] = useState(false);

  const config = ENTITY_CONFIGS[entityManager.entityType as EntityTypeKey];
  const record = project?.getCrudStore(config?.storeKey as string)?.getById(Number(entityManager.entityId)) as unknown as EntityRecord;
  useSwivuStore<EntityRecord>({  storeId: config.storeKey, subscriberFn: ({record: changedRecord}) => changedRecord.id === record?.id});

  // Get the appropriate context menu component for this entity type
  const ContextMenuComponent = config ? config?.contextMenu : undefined;

  // Clear any existing timeout
  const clearHoverTimeout = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  // Handle mouse enter on trigger
  const handleTriggerMouseEnter = () => {
    clearHoverTimeout();
    setIsHovering(true);
    setOpen(true);
  };

  // Handle mouse leave from trigger
  const handleTriggerMouseLeave = (e: React.MouseEvent) => {
    try {
      const relatedTarget = e.relatedTarget as Node | null;
      if (relatedTarget && contentRef.current?.contains(relatedTarget)) {
        return; // Don't close if moving to content
      }
    } catch (error) {
      console.warn('Error checking relatedTarget contains:', error);
    }

    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovering(false);
      setOpen(false);
    }, 500);
  };

  // Handle mouse enter on dropdown content
  const handleContentMouseEnter = () => {
    clearHoverTimeout();
    setIsHovering(true);
  };

  // Handle mouse leave from dropdown content
  const handleContentMouseLeave = (e: React.MouseEvent) => {
    try {
      const relatedTarget = e.relatedTarget as Node | null;
      if (relatedTarget && triggerRef.current?.contains(relatedTarget)) {
        return; // Don't close if moving back to trigger
      }
    } catch (error) {
      console.warn('Error checking relatedTarget contains:', error);
    }

    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovering(false);
      setOpen(false);
    }, 500);
  };

  // Handle dropdown open change (when clicking outside or pressing escape)
  const handleOpenChange = (openState: boolean) => {
    if (!openState && !isHovering) {
      setOpen(false);
    } else if (openState) {
      setOpen(true);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      clearHoverTimeout();
    };
  }, []);

  if (!config || !record || !entityManager.entityType) return null;

  return (
    <div className="flex items-center leading-normal w-full gap-2">
      {record?.color && <div
        className="size-2 rounded-full block flex-shrink-0"
        style={{ backgroundColor: record.color }}
      ></div>}

      <DropdownMenu open={open} onOpenChange={handleOpenChange} modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            ref={triggerRef}
            className={cn(
              "flex items-center gap-2 !p-0 rounded-md",
              "font-medium truncate max-w-[20rem]",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring swivu-header-menu-button"
            )}
            onMouseEnter={handleTriggerMouseEnter}
            onMouseLeave={handleTriggerMouseLeave}
          >
            <span className="truncate">{record?.name || 'Untitled'}</span>
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          ref={contentRef}
          align="start"
          className="w-56 z-[11001]"
          onMouseEnter={handleContentMouseEnter}
          onMouseLeave={handleContentMouseLeave}
        >
          {ContextMenuComponent && (
            <ContextMenuComponent
              record={record}
              onClose={() => {
                setIsHovering(false);
                setOpen(false);
              }}
              onItemClick={(item: { name: string }) => {
                if (item.name === 'copyTo') {
                  setOpen(false);
                  setShowCopyDialog(true);
                }
              }}
            />
          )}
        </DropdownMenuContent>
      </DropdownMenu>

       {/* Copy to Board Dialog */}
       <CopyToBoardDialog
        open={showCopyDialog}
        onOpenChange={setShowCopyDialog}
        record={record}
        entityType={entityManager.entityType}
        entityName={record?.name}
      />
    </div>
  );
}